import * as React from 'react';
import * as Sentry from '@sentry/react';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';
import { useAuth } from 'auth';
import { useGetCustomer } from 'api';
import { getOpenfieldRegion } from 'shared/utils';

interface FeatureFlagProviderProps {
    children: React.ReactNode;
}

/**
 * LaunchDarkly provider that initializes the SDK with user context from auth.
 * This component will show children once LaunchDarkly is initialized.
 * If no client-side ID is configured, it will render children without LaunchDarkly.
 */
export const FeatureFlagProvider: React.FC<FeatureFlagProviderProps> = ({ children }) => {
    const { user, isAuthenticated } = useAuth();
    const customer = useGetCustomer().data;
    const customerId = customer?.id;
    const customerAwsRegion = customer?.aws_region;

    const [Provider, setProvider] = React.useState<React.ComponentType<{
        children: React.ReactNode;
    }> | null>(null);
    const [isInitialized, setIsInitialized] = React.useState(false);
    const [initError, setInitError] = React.useState<Error | null>(null);

    React.useEffect(() => {
        const clientSideID = process.env.REACT_APP_LAUNCHDARKLY_CLIENT_ID;

        // If no client ID is configured, skip LaunchDarkly initialization
        if (!clientSideID) {
            setIsInitialized(true);
            return;
        }

        if (!isAuthenticated || !user || !customerId || !customerAwsRegion) {
            setProvider(null);
            return;
        }

        const initLaunchDarkly = async () => {
            try {
                // Set up user context for LaunchDarkly
                const region = getOpenfieldRegion(customerAwsRegion)?.toLowerCase();
                const context = {
                    kind: 'customer',
                    key: `ofc-${customerId}-${region ?? 'unknown'}`,
                };

                const proxyUrl = process.env.REACT_APP_LAUNCHDARKLY_PROXY_URL;
                const provider = await asyncWithLDProvider({
                    clientSideID,
                    context,
                    options: {
                        // Enable streaming for real-time flag updates
                        streaming: true,
                        // Bootstrap from localStorage for faster initialization
                        bootstrap: 'localStorage',
                        // china only
                        ...(proxyUrl && { baseUrl: proxyUrl, streamUrl: proxyUrl, eventsUrl: proxyUrl }),
                        // feeds flag evaluations to Sentry's launchDarklyIntegration so errors can be filtered by flag
                        inspectors: [Sentry.buildLaunchDarklyFlagUsedHandler()],
                    },
                });

                setProvider(() => provider);
                setIsInitialized(true);
            } catch (error) {
                setInitError(error as Error);
                setIsInitialized(true);
            }
        };

        initLaunchDarkly();
    }, [user, isAuthenticated, customerId, customerAwsRegion]);

    if (!isInitialized) {
        return <>{children}</>;
    }

    if (initError || !Provider) {
        return <>{children}</>;
    }

    // Render children wrapped with the feature flag provider
    return <Provider>{children}</Provider>;
};
